import CryptoJS from 'crypto-js';

export class Fingerprinter {
    constructor() {
        this.startTime = 0;
    };

    async generate(includeTimestamp = false) {
        this.startTime = performance.now();
        const basicData = {
            userAgent: this.getUserAgent(),
            language: this.getLanguage(),
            platform: this.getPlatform(),
            timezone: this.getTimezone(),
            cookieEnabled: this.getCookieEnabled(),
            screenResolution: this.getScreenResolution(),
            colorDepth: this.getColorDepth(),
            pixelRatio: this.getPixelRatio(),
            availableSize: this.getAvailableSize(),
            orientation: this.getOrientation(),
            cpuCores: this.getCpuCores(),
            memory: this.getMemory(),
            touchSupport: this.getTouchSupport(),
            webglVendor: this.getWebGLVendor(),
            webglRenderer: this.getWebGLRenderer(),
            canvasFingerprint: this.getCanvasFingerprint(),
            plugins: this.getPlugins(),
            mimeTypes: this.getMimeTypes(),
            localStorage: this.hasLocalStorage(),
            sessionStorage: this.hasSessionStorage(),
            indexedDB: this.hasIndexedDB(),
            fontsDetected: this.getFonts()
        };
        if (includeTimestamp) basicData.timestamp = new Date().toISOString();
        let audioFingerprint;
        try {
            audioFingerprint = await Promise.race([this.getAudioFingerprint(), new Promise((resolve) => {setTimeout(() => resolve('timeout'), 1000)})]);
        } catch (e) {
            audioFingerprint = 'error';
        }
        const data = {...basicData, audioFingerprint};
        const fingerprintString = JSON.stringify(data);
        const hash = CryptoJS.SHA256(fingerprintString).toString();
        const generationTime = Math.round(performance.now() - this.startTime);
        return {
            hash,
            generationTime,
            dataPoints: Object.keys(data).length,
            data: data
        };
    }

    getUserAgent() {
        return navigator.userAgent;
    };

    getLanguage() {
        return navigator.language || navigator.browserLanguage || 'en-US';
    };

    getPlatform() {
        return navigator.platform;
    };

    getTimezone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    };

    getCookieEnabled() {
        return navigator.cookieEnabled;
    };

    getScreenResolution() {
        return `${screen.width}×${screen.height}`;
    };

    getColorDepth() {
        return screen.colorDepth;
    };

    getPixelRatio() {
        return window.devicePixelRatio || 1;
    };

    getAvailableSize() {
        return `${screen.availWidth}×${screen.availHeight}`;
    };

    getOrientation() {
        return screen.width > screen.height ? 'landscape' : 'portrait';
    };

    getCpuCores() {
        return navigator.hardwareConcurrency || 1;
    };

    getMemory() {
        const memory = navigator.deviceMemory;
        return memory ? `${memory} GB` : 'Unknown';
    };

    getTouchSupport() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };

    getWebGLVendor() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl) {
                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                if (debugInfo) {
                    return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'Unknown';
                }
            } else return 'Unknown';
        } catch (e) {
            return 'Unknown';
        }
    };

    getWebGLRenderer() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl) {
                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                if (debugInfo) {
                    return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'Unknown';
                }
            } else return 'Unknown';
        } catch (e) {
            return 'Unknown';
        }
    };

    getCanvasFingerprint() {
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return 'Unavailable';
            canvas.width = 200;
            canvas.height = 50;
            // Draw some text
            ctx.textBaseline = 'top';
            ctx.font = '14px Arial';
            ctx.fillStyle = '#f60';
            ctx.fillRect(125, 1, 62, 20);
            ctx.fillStyle = '#069';
            ctx.fillText('Canvas fingerprint', 2, 15);
            ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
            ctx.fillText('Canvas fingerprint', 4, 17);
            // Draw some shapes
            ctx.globalCompositeOperation = 'multiply';
            ctx.fillStyle = 'rgb(255,0,255)';
            ctx.beginPath();
            ctx.arc(50, 50, 50, 0, Math.PI * 2, true);
            ctx.closePath();
            ctx.fill();
            return CryptoJS.SHA256(canvas.toDataURL()).toString().substring(0, 24);
        } catch (e) {
            return 'Unavailable';
        }
    };

    getPlugins() {
        const plugins = [];
        for (let i = 0; i < navigator.plugins.length; i++) {
            plugins.push(navigator.plugins[i].name);
        }
        return plugins;
    };

    getMimeTypes() {
        const mimeTypes = [];
        for (let i = 0; i < navigator.mimeTypes.length; i++) {
            mimeTypes.push(navigator.mimeTypes[i].type);
        }
        return mimeTypes;
    };

    hasLocalStorage() {
        try {
            return typeof localStorage !== 'undefined';
        } catch (e) {
            return false;
        }
    };

    hasSessionStorage() {
        try {
            return typeof sessionStorage !== 'undefined';
        } catch (e) {
            return false;
        }
    };

    hasIndexedDB() {
        try {
            return typeof indexedDB !== 'undefined';
        } catch (e) {
            return false;
        }
    };

    getFonts() {
        const baseFonts = ['monospace', 'sans-serif', 'serif'];
        const testFonts = ['Arial', 'Arial Black', 'Arial Narrow', 'Arial Rounded MT Bold', 'Calibri', 'Cambria', 'Comic Sans MS', 'Courier', 'Courier New', 'Garamond', 'Georgia', 'Helvetica', 'Impact', 'Lucida Console', 'Lucida Sans Unicode', 'Microsoft Sans Serif', 'Palatino', 'Times', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Monaco', 'Consolas', 'SF Pro Display', 'Roboto', 'Open Sans'];
        const testSize = '72px';
        const testString = 'mmmmmmmmmmlli';        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return 0;
        const defaultWidths = {};
        baseFonts.forEach(baseFont => {
            context.font = testSize + ' ' + baseFont;
            defaultWidths[baseFont] = context.measureText(testString).width;
        });
        let detectedFonts = 0;
        testFonts.forEach((testFont) => {
            let isDifferent = false;
            baseFonts.forEach((baseFont) => {
                context.font = testSize + ' ' + testFont + ', ' + baseFont;
                const testFontWidth = context.measureText(testString).width;
                const baseFontWidth = defaultWidths[baseFont];
                if (testFontWidth !== baseFontWidth) isDifferent = true;
            });
            if (isDifferent) detectedFonts++;
        });
        return detectedFonts;
    };

    async getAudioFingerprint() {
        return new Promise((resolve) => {
            const audioContext = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100, 44100);
            const oscillator = audioContext.createOscillator();
            oscillator.type = 'triangle';
            oscillator.frequency.value = 10000;
            const compressor = audioContext.createDynamicsCompressor();
            compressor.threshold.value = -50;
            compressor.knee.value = 40;
            compressor.ratio.value = 12;
            compressor.attack.value = 0;
            compressor.release.value = 0.25;
            oscillator.connect(compressor);
            compressor.connect(audioContext.destination);
            oscillator.start(0);
            audioContext.startRendering().then((buffer) => {
                let fingerprint = '';
                const channelData = buffer.getChannelData(0);
                for (let i = 4500; i < 5000; i++) {
                    fingerprint += Math.abs(channelData[i]).toFixed(3);
                }
                const hash = CryptoJS.SHA256(fingerprint).toString().substring(0, 24);
                resolve(hash);
            });
        });
    };
}

export const fingerprinter = new Fingerprinter();